// src/components/MapPickerLeaflet/MapPickerLeaflet.jsx
import React, { useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "./mapPickerLeaflet.css";
import { forwardGeocode, reverseGeocode } from "../../../../Utilities/geocode/geocode";

// ⚠️ Asegúrate de importar 'leaflet/dist/leaflet.css' una sola vez en tu app (p.ej. index.jsx)
// y de hacer el mergeOptions de iconos una sola vez globalmente si puedes.
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function ClickHandler({ onSelect }) {
    useMapEvents({
        click(e) {
            onSelect(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
}

// Recentrado controlado por prop
function Recenter({ center, zoom = 14 }) {
    const map = useMap();
    map.setView(center, zoom);
    return null;
}

export default function MapPickerLeaflet({
    initialLat = 19.432608,
    initialLng = -99.133209,
    geocodeEmail,                // opcional para Nominatim
    onPick,                      // (lat, lng, address?) => void
    onClose,
}) {
    const [sel, setSel] = useState({ lat: initialLat, lng: initialLng });
    const [search, setSearch] = useState("");
    const [addressPreview, setAddressPreview] = useState("");
    const [busy, setBusy] = useState(false);

    const markerRef = useRef(null);
    const mapRef = useRef(null);             // ← tendremos la instancia del mapa

    const position = useMemo(
        () => L.latLng(Number(sel.lat) || 19.432608, Number(sel.lng) || -99.133209),
        [sel.lat, sel.lng]
    );

    const centerMap = (lat, lng, z = 15) => {
        // seguridad: si el mapa ya existe, centramos
        if (mapRef.current) {
            mapRef.current.setView([lat, lng], z, { animate: true });
        }
    };

    const onDragEnd = () => {
        const m = markerRef.current;
        if (m && m.getLatLng) {
            const p = m.getLatLng();
            setSel({ lat: p.lat, lng: p.lng });
            setAddressPreview("");
            centerMap(p.lat, p.lng);
        }
    };

    const geolocate = () => {
        if (!navigator.geolocation) {
            setAddressPreview("Geolocalización no soportada por el navegador");
            return;
        }
        setBusy(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setSel(p);
                setAddressPreview("");           // se calculará al confirmar
                centerMap(p.lat, p.lng, 16);     // ← centramos de inmediato
                setBusy(false);
            },
            (err) => {
                console.error(err);
                setAddressPreview("No se pudo obtener tu ubicación (permiso denegado o error).");
                setBusy(false);
            },
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    const doSearch = async () => {
        const q = (search || "").trim();
        if (!q) return;
        try {
            setBusy(true);
            const results = await forwardGeocode(q, { email: geocodeEmail });
            if (results[0]) {
                const { lat, lon, display_name } = results[0];
                const fLat = Number(lat), fLng = Number(lon);
                setSel({ lat: fLat, lng: fLng });
                setAddressPreview(display_name || "");
                centerMap(fLat, fLng, 16);       // ← centramos al resultado
            } else {
                setAddressPreview("No se encontró esa dirección");
            }
        } catch (e) {
            console.error(e);
            setAddressPreview("Error buscando esa dirección");
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        // Al confirmar, intentamos reverse; si falla, devolvemos coords igual.
        setBusy(true);
        try {
            let addr = addressPreview;
            if (!addr) {
                try {
                    const r = await reverseGeocode(sel.lat, sel.lng, { email: geocodeEmail });
                    addr = r?.display_name || "";
                } catch (e) {
                    console.warn("Reverse geocode falló, usando coords sin dirección.");
                }
            }
            onPick?.(sel.lat, sel.lng, addr);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mpl-backdrop" onClick={onClose}>
            <div className="mpl-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mpl-header">
                    <h3>Elegir ubicación</h3>
                    <button className="mpl-close" onClick={onClose}>✕</button>
                </div>

                <div className="mpl-toolbar">
                    <div style={{ display: "flex", gap: 8, flex: 1 }}>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar dirección (calle, ciudad...)"
                            className="mpl-input"
                        />
                        <button className="mpl-btn" onClick={doSearch} disabled={busy}>Buscar</button>
                        <button className="mpl-btn" onClick={geolocate} disabled={busy}>Mi ubicación</button>
                    </div>
                    <div className="mpl-coords">
                        <span>Lat: <strong>{Number(sel.lat).toFixed(6)}</strong></span>
                        <span>Lng: <strong>{Number(sel.lng).toFixed(6)}</strong></span>
                    </div>
                </div>

                {addressPreview && (
                    <div className="mpl-preview">
                        <small className="mpl-preview-label">Dirección detectada:</small>
                        <div className="mpl-preview-text">{addressPreview}</div>
                    </div>
                )}

                <div className="mpl-map">
                    <MapContainer
                        center={position}
                        zoom={14}
                        scrollWheelZoom
                        style={{ height: "100%", width: "100%" }}
                        whenCreated={(map) => {            // ← guardamos instancia del mapa
                            mapRef.current = map;
                            setTimeout(() => map.invalidateSize(), 0); // por si el modal se abrió recién
                        }}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                        />
                        <Recenter center={position} />
                        <ClickHandler onSelect={(lat, lng) => {
                            setSel({ lat, lng });
                            setAddressPreview("");
                            centerMap(lat, lng);
                        }} />
                        <Marker
                            position={position}
                            draggable
                            // ref de forma callback para asegurar la instancia real del Marker
                            ref={(ref) => { markerRef.current = ref; }}
                            eventHandlers={{ dragend: onDragEnd }}
                        />
                    </MapContainer>
                </div>

                <div className="mpl-footer">
                    <button className="mpl-btn mpl-primary" onClick={confirm} disabled={busy}>
                        Usar esta ubicación
                    </button>
                    <button className="mpl-btn" onClick={onClose} disabled={busy}>Cancelar</button>
                </div>
            </div>
        </div>
    );
}
